import React, { useState, useEffect } from 'react';
import { useShop } from '../contexts/ShopContext';
import { subscriptionService, SubscriptionStatusResponse, SubscriptionPricingResponse, TierPricing } from '../services/subscriptionService';
import { rateLimitService, isUnlimitedValue, resourceTypesIn } from '../services/rateLimitService';
import { RateLimitCheckResult, ResourceType, RateLimitStatuses } from '../types';
import { formatDateTime } from '../utils/dateUtils';

interface SubscriptionManagerProps {
  shopId: string;
}

interface TierComparisonModalProps {
  tiers: TierPricing[];
  currentTierType?: string;
  onClose: () => void;
  onSubscribe: (priceId: string) => void;
  isLoading: boolean;
}

// Modal component for showing trial details before activation
interface TrialModalProps {
  trialDurationDays: number;
  trialLimits: {
    maxSlides: number;
    maxActiveMembers: number;
    maxActiveClientMembers?: number;
    maxShopLocations: number;
    maxShopPlaces: number;
    maxMarketplaceEvalsPerMonth: number;
    maxQuickAnalyzesPerMonth: number;
  };
  onClose: () => void;
  onStartTrial: () => void;
  isLoading: boolean;
}

const TrialModal: React.FC<TrialModalProps> = ({ trialDurationDays, trialLimits, onClose, onStartTrial, isLoading }) => {
  const formatLimit = (value: number): string => {
    if (isUnlimitedValue(value)) return 'Unlimited';
    return value.toLocaleString();
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1100,
        padding: '20px',
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'var(--bg-primary)',
          borderRadius: '12px',
          padding: '24px',
          maxWidth: '440px',
          width: '100%',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0, fontSize: '24px', fontWeight: 600, color: 'var(--text-primary)' }}>
            Start Free Trial
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: 'var(--text-secondary)',
              padding: '4px',
            }}
          >
            ×
          </button>
        </div>

        {/* Trial Duration Badge */}
        <div
          style={{
            backgroundColor: 'var(--bg-accent)',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '20px',
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: '36px', fontWeight: 700, color: 'var(--primary-light)' }}>
            {trialDurationDays}
          </div>
          <div style={{ fontSize: '14px', color: 'var(--primary-light)', fontWeight: 500 }}>
            Day Free Trial
          </div>
        </div>

        {/* Description */}
        <p style={{ fontSize: '14px', color: 'var(--text-primary)', marginBottom: '20px', lineHeight: 1.5 }}>
          Experience all features with no credit card required. Your trial includes:
        </p>

        {/* Resource Limits */}
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: '8px' }}>
            Resources
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', padding: '6px 0', borderBottom: '1px solid var(--border-light)' }}>
            <span style={{ color: 'var(--text-primary)' }}>Slides</span>
            <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{formatLimit(trialLimits.maxSlides)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', padding: '6px 0', borderBottom: '1px solid var(--border-light)' }}>
            <span style={{ color: 'var(--text-primary)' }}>Team Members</span>
            <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{formatLimit(trialLimits.maxActiveMembers)}</span>
          </div>
          {trialLimits.maxActiveClientMembers !== undefined && (
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', padding: '6px 0', borderBottom: '1px solid var(--border-light)' }}>
              <span style={{ color: 'var(--text-primary)' }}>Client Members</span>
              <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{formatLimit(trialLimits.maxActiveClientMembers)}</span>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', padding: '6px 0', borderBottom: '1px solid var(--border-light)' }}>
            <span style={{ color: 'var(--text-primary)' }}>Locations</span>
            <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{formatLimit(trialLimits.maxShopLocations)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', padding: '6px 0', borderBottom: '1px solid var(--border-light)' }}>
            <span style={{ color: 'var(--text-primary)' }}>Places</span>
            <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{formatLimit(trialLimits.maxShopPlaces)}</span>
          </div>
        </div>

        {/* Usage Limits */}
        <div style={{ marginBottom: '24px' }}>
          <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: '8px' }}>
            Monthly Usage
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', padding: '6px 0', borderBottom: '1px solid var(--border-light)' }}>
            <span style={{ color: 'var(--text-primary)' }}>Marketplace Evals</span>
            <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{formatLimit(trialLimits.maxMarketplaceEvalsPerMonth)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', padding: '6px 0', borderBottom: '1px solid var(--border-light)' }}>
            <span style={{ color: 'var(--text-primary)' }}>Quick Analyzes</span>
            <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{formatLimit(trialLimits.maxQuickAnalyzesPerMonth)}</span>
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={onClose}
            style={{
              flex: 1,
              padding: '12px 20px',
              backgroundColor: 'var(--bg-secondary)',
              color: 'var(--text-primary)',
              border: 'none',
              borderRadius: '8px',
              fontSize: '15px',
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={onStartTrial}
            disabled={isLoading}
            style={{
              flex: 1,
              padding: '12px 20px',
              backgroundColor: 'var(--primary)',
              color: 'var(--text-inverse)',
              border: 'none',
              borderRadius: '8px',
              fontSize: '15px',
              fontWeight: 600,
              cursor: isLoading ? 'not-allowed' : 'pointer',
              opacity: isLoading ? 0.7 : 1,
            }}
          >
            {isLoading ? 'Starting...' : 'Start Free Trial'}
          </button>
        </div>

        {/* Footer note */}
        <p style={{ margin: '16px 0 0', fontSize: '12px', color: 'var(--text-secondary)', textAlign: 'center' }}>
          No credit card required.
        </p>
      </div>
    </div>
  );
};

// Modal component for comparing all tiers side-by-side
const TierComparisonModal: React.FC<TierComparisonModalProps> = ({ tiers, currentTierType, onClose, onSubscribe, isLoading }) => {
  // Format limit value for display
  const formatLimit = (value: number): string => {
    if (isUnlimitedValue(value)) return 'Unlimited';
    return value.toLocaleString();
  };

  // Sort tiers by price
  const sortedTiers = [...tiers].sort((a, b) => a.monthlyPriceUSD - b.monthlyPriceUSD);

  // Find the current tier's price for upgrade/downgrade determination
  const currentTierPrice = currentTierType
    ? sortedTiers.find(t => t.tierType === currentTierType)?.monthlyPriceUSD ?? null
    : null;

  const getButtonLabel = (tier: TierPricing, isCurrentPlan: boolean): string => {
    if (isCurrentPlan) return 'Current Plan';
    if (isLoading) return '...';
    if (currentTierPrice === null) return 'Subscribe';
    return tier.monthlyPriceUSD > currentTierPrice ? 'Upgrade' : 'Downgrade';
  };

  // Limit labels for the comparison rows
  const resourceLimits = [
    { key: 'maxSlides', label: 'Slides' },
    { key: 'maxActiveMembers', label: 'Team Members' },
    { key: 'maxActiveClientMembers', label: 'Client Members' },
    { key: 'maxShopLocations', label: 'Locations' },
    { key: 'maxShopPlaces', label: 'Places' },
  ];

  const usageLimits = [
    { key: 'maxMarketplaceEvalsPerMonth', label: 'Marketplace Evals' },
    { key: 'maxQuickAnalyzesPerMonth', label: 'Quick Analyzes' },
  ];

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px',
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'var(--bg-primary)',
          borderRadius: '12px',
          padding: '24px',
          maxWidth: '800px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h2 style={{ margin: 0, fontSize: '24px', fontWeight: 600, color: 'var(--text-primary)' }}>
            Choose Your Plan
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: 'var(--text-secondary)',
              padding: '4px',
            }}
          >
            ×
          </button>
        </div>

        {/* Tier Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${sortedTiers.length}, 1fr)`, gap: '16px', marginBottom: '16px' }}>
          {sortedTiers.map((tier, index) => {
            const isCurrentPlan = currentTierType === tier.tierType;
            const isHighlighted = currentTierType ? isCurrentPlan : index === 1;
            return (
            <div
              key={tier.tierType}
              style={{
                backgroundColor: isHighlighted ? 'var(--bg-secondary)' : 'var(--bg-primary)',
                border: isHighlighted ? '2px solid var(--primary)' : '1px solid var(--border-light)',
                borderRadius: '12px',
                padding: '20px',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {/* Tier Header */}
              <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                {/* Fixed height badge area for alignment */}
                <div style={{
                  height: '18px',
                  marginBottom: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  {isHighlighted && (
                    <span style={{
                      fontSize: '11px',
                      fontWeight: 600,
                      color: 'var(--text-accent)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}>
                      {isCurrentPlan ? 'Current Plan' : 'Most Popular'}
                    </span>
                  )}
                </div>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: 'var(--text-primary)' }}>
                  {tier.displayName}
                </h3>
                <div style={{ fontSize: '28px', fontWeight: 700, color: 'var(--text-accent)', marginTop: '8px' }}>
                  ${tier.monthlyPriceUSD}
                  <span style={{ fontSize: '14px', fontWeight: 400, color: 'var(--text-secondary)' }}>/mo</span>
                </div>
              </div>

              {/* Resource Limits */}
              <div style={{ marginBottom: '12px' }}>
                <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: '8px' }}>
                  Resources
                </div>
                {resourceLimits.map((limit) => (
                  <div key={limit.key} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', padding: '4px 0', borderBottom: '1px solid var(--border-light)' }}>
                    <span style={{ color: 'var(--text-primary)' }}>{limit.label}</span>
                    <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{formatLimit((tier.limits as any)[limit.key])}</span>
                  </div>
                ))}
              </div>

              {/* Usage Limits */}
              <div style={{ marginBottom: '16px', flex: 1 }}>
                <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: '8px' }}>
                  Monthly Usage
                </div>
                {usageLimits.map((limit) => (
                  <div key={limit.key} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', padding: '4px 0', borderBottom: '1px solid var(--border-light)' }}>
                    <span style={{ color: 'var(--text-primary)' }}>{limit.label}</span>
                    <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{formatLimit((tier.limits as any)[limit.key])}</span>
                  </div>
                ))}
              </div>

              {/* Subscribe Button */}
              <button
                onClick={() => onSubscribe(tier.stripePriceId)}
                disabled={isLoading || isCurrentPlan}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  backgroundColor: isCurrentPlan ? 'var(--border-light)' : isHighlighted ? 'var(--primary)' : 'transparent',
                  color: isCurrentPlan ? 'var(--text-secondary)' : isHighlighted ? 'var(--text-inverse)' : 'var(--text-accent)',
                  border: isCurrentPlan ? 'none' : isHighlighted ? 'none' : '2px solid var(--primary)',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: isLoading || isCurrentPlan ? 'not-allowed' : 'pointer',
                  opacity: isLoading ? 0.7 : 1,
                  transition: 'all 0.15s ease',
                }}
              >
                {getButtonLabel(tier, isCurrentPlan)}
              </button>
            </div>
          );
          })}
        </div>

        <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-secondary)', textAlign: 'center' }}>
          {currentTierType
            ? 'Plan changes are prorated automatically.'
            : "You'll be redirected to Stripe to complete payment securely."}
        </p>
      </div>
    </div>
  );
};

const SubscriptionManager: React.FC<SubscriptionManagerProps> = ({ shopId }) => {
  const { currentShop, currentMembership, refreshShops } = useShop();
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Subscription state
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatusResponse | null>(null);
  const [rateLimits, setRateLimits] = useState<RateLimitStatuses>({});
  const [pricing, setPricing] = useState<SubscriptionPricingResponse | null>(null);
  const [showTierComparison, setShowTierComparison] = useState(false);
  const [showTrialModal, setShowTrialModal] = useState(false);
  const [pendingPlanChange, setPendingPlanChange] = useState<TierPricing | null>(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  // Check if user is owner (required for subscription management)
  const isOwner = currentMembership?.roles.includes('OWNER') || false;

  // Load subscription status and rate limits
  useEffect(() => {
    loadData();
  }, [shopId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      const [statusResponse, limitsResponse, pricingResponse] = await Promise.all([
        subscriptionService.getSubscriptionStatus(shopId).catch(() => null),
        rateLimitService.getAllRateLimits(shopId).catch(() => ({})),
        subscriptionService.getPricing().catch(() => null),
      ]);

      setSubscriptionStatus(statusResponse);
      setRateLimits(limitsResponse);
      setPricing(pricingResponse);
    } catch (error: any) {
      console.error('Error loading subscription data:', error);
      setError('Failed to load subscription information');
    } finally {
      setLoading(false);
    }
  };

  const handleActivateTrial = async () => {
    if (!isOwner) return;

    try {
      setActionLoading(true);
      setError('');
      setSuccessMessage('');

      await subscriptionService.activateTrial(shopId);
      setSuccessMessage('Free trial activated successfully!');

      // Refresh data
      await loadData();
      await refreshShops();

      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (error: any) {
      console.error('Failed to activate trial:', error);
      setError(error.message || 'Failed to activate trial');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpgrade = async (priceId: string) => {
    if (!isOwner || !priceId) return;

    try {
      setActionLoading(true);
      setError('');

      // If shop already has an active Stripe subscription, use the upgrade API
      if (subscriptionStatus?.stripeSubscriptionId) {
        await subscriptionService.upgradeSubscription(shopId, priceId);
        setSuccessMessage('Plan changed successfully!');
        await loadData();
        await refreshShops();
        setActionLoading(false);
        setTimeout(() => setSuccessMessage(''), 5000);
        return;
      }

      // Otherwise, create a new checkout session for first-time subscription
      const response = await subscriptionService.createCheckoutSession(shopId, priceId);

      // Redirect to Stripe Checkout
      if (response.url) {
        window.location.href = response.url;
      }
    } catch (error: any) {
      console.error('Failed to upgrade subscription:', error);
      setError(error.message || 'Failed to upgrade subscription');
      setActionLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    if (!isOwner) return;

    try {
      setActionLoading(true);
      setError('');

      const response = await subscriptionService.createBillingPortalSession(shopId);

      // Redirect to Stripe Billing Portal
      if (response.url) {
        window.location.href = response.url;
      }
    } catch (error: any) {
      console.error('Failed to create billing portal session:', error);
      setError(error.message || 'Failed to open billing portal');
      setActionLoading(false);
    }
  };

  const handleCancelSubscription = () => {
    if (!isOwner) return;
    setShowCancelConfirm(true);
  };

  const confirmCancelSubscription = async () => {
    setShowCancelConfirm(false);

    try {
      setActionLoading(true);
      setError('');
      setSuccessMessage('');

      await subscriptionService.cancelSubscription(shopId, false);
      setSuccessMessage('Subscription will be canceled at the end of the billing period.');

      await loadData();
      await refreshShops();

      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (error: any) {
      console.error('Failed to cancel subscription:', error);
      setError(error.message || 'Failed to cancel subscription');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivate = async () => {
    if (!isOwner) return;

    try {
      setActionLoading(true);
      setError('');
      setSuccessMessage('');

      await subscriptionService.reactivateSubscription(shopId);
      setSuccessMessage('Subscription reactivated successfully!');

      await loadData();
      await refreshShops();

      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (error: any) {
      console.error('Failed to reactivate subscription:', error);
      setError(error.message || 'Failed to reactivate subscription');
    } finally {
      setActionLoading(false);
    }
  };

  // Get status color - based on status string for consistency with getStatusLabel
  const getStatusColor = (): { bg: string; text: string; border: string } => {
    if (!subscriptionStatus || !subscriptionStatus.status) {
      if (subscriptionStatus?.isEligibleForTrial) {
        return { bg: 'var(--bg-primary)', text: 'var(--text-accent)', border: 'var(--border-light)' }; // Gray for guest
      }
      return { bg: 'var(--error-bg)', text: 'var(--error-text)', border: 'var(--error-border)' }; // Red for expired
    }

    // Expired trial — show muted gray (Guest)
    if (subscriptionStatus.isTrialExpired) {
      return { bg: 'var(--bg-primary)', text: 'var(--text-accent)', border: 'var(--border-light)' };
    }

    switch (subscriptionStatus.status) {
      case 'active':
        if (subscriptionStatus.cancelAtPeriodEnd) {
          return { bg: 'var(--warning-bg)', text: 'var(--warning-text)', border: 'var(--warning)' }; // Orange for canceling
        }
        return { bg: 'var(--success-bg)', text: 'var(--success-dark)', border: 'var(--success)' }; // Green for active
      case 'trialing':
        return { bg: 'var(--bg-accent)', text: 'var(--primary-light)', border: 'var(--primary-light)' }; // Blue for trial
      case 'past_due':
        return { bg: 'var(--warning-bg)', text: 'var(--warning-text)', border: 'var(--warning)' }; // Orange for past due
      case 'canceled':
      case 'incomplete':
      case 'trial_expired':
      default:
        return { bg: 'var(--error-bg)', text: 'var(--error-text)', border: 'var(--error-border)' }; // Red for canceled/expired
    }
  };

  // Get status label
  const getStatusLabel = (): string => {
    if (!subscriptionStatus || !subscriptionStatus.status) {
      if (subscriptionStatus?.isEligibleForTrial) {
        return 'Guest';
      }
      return 'Trial Expired';
    }

    // Check computed flags first — an expired trial still has status "trialing"
    // but the backend sets isTrialExpired=true when the trial end date has passed.
    if (subscriptionStatus.isTrialExpired) {
      return 'Guest';
    }

    switch (subscriptionStatus.status) {
      case 'trialing':
        return 'Free Trial';
      case 'active':
        return subscriptionStatus.cancelAtPeriodEnd ? 'Active (Canceling)' : 'Active';
      case 'past_due':
        return 'Past Due';
      case 'canceled':
        return 'Canceled';
      case 'incomplete':
        return 'Incomplete';
      case 'trial_expired':
        return 'Trial Expired';
      default:
        return 'Unknown';
    }
  };

  // Get trial days remaining
  const getTrialDaysRemaining = (): number | null => {
    if (!subscriptionStatus?.trialEnd) return null;

    try {
      const trialEnd = new Date(subscriptionStatus.trialEnd);
      const now = new Date();
      const diffMs = trialEnd.getTime() - now.getTime();
      const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
      return Math.max(0, diffDays);
    } catch {
      return null;
    }
  };

  // Render usage meter
  const renderUsageMeter = (result: RateLimitCheckResult) => {
    const percentage = rateLimitService.getUsagePercentage(result);
    const color = rateLimitService.getUsageColor(result);
    // Healthy usage is gold, not green: this bar reports HEADROOM, not system health, and green
    // competed with the genuine status signals elsewhere on the screen (the Active badge). Mirrors
    // Theme.gold / Theme.warning / Theme.error in SubscriptionView on iOS.
    const colorMap = {
      gold: 'var(--gold)',
      warning: 'var(--warning)',
      error: 'var(--error)',
    };

    return (
      <div style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
          <span style={{ fontSize: '14px', color: 'var(--text-primary)' }}>
            {rateLimitService.getResourceLabel(result.resourceType)}
          </span>
          <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
            {rateLimitService.getUsageDisplay(result)}
          </span>
        </div>
        <div
          style={{
            height: '8px',
            backgroundColor: 'var(--border-light)',
            borderRadius: '4px',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              height: '100%',
              width: `${percentage}%`,
              backgroundColor: colorMap[color],
              borderRadius: '4px',
              transition: 'width 0.3s ease',
            }}
          />
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="subscription-manager-loading">
        <div className="flex items-center justify-center py-12">
          <div className="spinner-sm mr-3"></div>
          <span className="text-muted">Loading subscription information...</span>
        </div>
      </div>
    );
  }

  const statusColor = getStatusColor();
  const trialDays = getTrialDaysRemaining();

  return (
    <div className="subscription-manager">
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h3 className="heading-secondary" style={{ fontSize: '18px', marginBottom: '4px' }}>
          Subscription & Usage
        </h3>
        <p className="text-muted" style={{ fontSize: '14px' }}>
          Manage your subscription and monitor your usage limits
        </p>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div
          className="card"
          style={{
            backgroundColor: 'var(--success-bg)',
            borderColor: 'var(--success)',
            marginBottom: '24px',
            padding: '12px 16px',
          }}
        >
          <div style={{ color: 'var(--success-dark)', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>✓</span>
            <span style={{ fontSize: '14px' }}>{successMessage}</span>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div
          className="card"
          style={{
            marginBottom: '24px',
            padding: '12px 16px',
            backgroundColor: 'var(--error-bg)',
            borderColor: 'var(--error)',
          }}
        >
          <div style={{ color: 'var(--error-text)', fontSize: '14px' }}>{error}</div>
        </div>
      )}

      {/* Current Plan Card */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <div style={{ padding: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
            <div>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                Current Plan
              </div>
              <div style={{ fontSize: '24px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '8px' }}>
                {currentShop?.type || 'PERSONAL'}
              </div>
              <div
                style={{
                  display: 'inline-block',
                  padding: '4px 12px',
                  borderRadius: '16px',
                  fontSize: '12px',
                  fontWeight: 500,
                  backgroundColor: statusColor.bg,
                  color: statusColor.text,
                  border: `1px solid ${statusColor.border}`,
                }}
              >
                {getStatusLabel()}
              </div>
            </div>

            {/* Trial countdown — active trial only */}
            {subscriptionStatus?.isTrialing && !subscriptionStatus?.isTrialExpired && trialDays !== null && (
              <div
                style={{
                  textAlign: 'right',
                  padding: '12px 16px',
                  backgroundColor: 'var(--bg-accent)',
                  borderRadius: '8px',
                }}
              >
                <div style={{ fontSize: '32px', fontWeight: 700, color: 'var(--primary-light)' }}>
                  {trialDays}
                </div>
                <div style={{ fontSize: '12px', color: 'var(--primary-light)' }}>
                  days left in trial
                </div>
              </div>
            )}
            {/* Trial expired indicator */}
            {subscriptionStatus?.isTrialExpired && (
              <div
                style={{
                  textAlign: 'right',
                  padding: '12px 16px',
                  backgroundColor: 'var(--bg-secondary)',
                  borderRadius: '8px',
                }}
              >
                <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-secondary)' }}>
                  Trial expired
                </div>
              </div>
            )}
          </div>

          {/* Subscription details - show for active trial, expired trial, or paid subscriptions */}
          {subscriptionStatus && (subscriptionStatus.currentPeriodEnd || subscriptionStatus.trialEnd) && (() => {
            // Apple IAP subscription currently in its promotional free offer period
            // (e.g., Personal with first month free). Status is already "active" but the user
            // hasn't been charged yet — firstPaidAt is null and trialEnd tells us when Apple
            // will first bill them.
            const isAppleIntroOffer =
              subscriptionStatus.billingProvider === 'apple' &&
              !subscriptionStatus.firstPaidAt &&
              !!subscriptionStatus.trialEnd;

            return (
              <div
                style={{
                  marginTop: '20px',
                  paddingTop: '20px',
                  borderTop: '1px solid var(--border-light)',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                  gap: '16px',
                }}
              >
                {/* Period Start */}
                {subscriptionStatus.currentPeriodStart && (
                  <div>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '2px' }}>
                      {(subscriptionStatus.isTrialing || subscriptionStatus.isTrialExpired) ? 'Trial Started' : 'Current Period Start'}
                    </div>
                    <div style={{ fontSize: '14px', color: 'var(--text-primary)' }}>
                      {formatDateTime(subscriptionStatus.currentPeriodStart)}
                    </div>
                  </div>
                )}
                {/* Apple IAP introductory offer: show "First Month Free" + "First Payment" */}
                {isAppleIntroOffer && subscriptionStatus.trialEnd && (
                  <div>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '2px' }}>
                      First Month Free
                    </div>
                    <div style={{ fontSize: '14px', color: 'var(--text-primary)' }}>
                      First payment {formatDateTime(subscriptionStatus.trialEnd)}
                    </div>
                  </div>
                )}
                {/* Next Billing Date - paid subscriptions not in intro offer and not in CSAI trial */}
                {subscriptionStatus.currentPeriodEnd && !isAppleIntroOffer && !subscriptionStatus.isTrialing && !subscriptionStatus.isTrialExpired && (
                  <div>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '2px' }}>
                      {subscriptionStatus.cancelAtPeriodEnd ? 'Access Until' : 'Next Billing Date'}
                    </div>
                    <div style={{ fontSize: '14px', color: 'var(--text-primary)' }}>
                      {formatDateTime(subscriptionStatus.currentPeriodEnd)}
                    </div>
                  </div>
                )}
                {/* CSAI-managed Trial End Date - only for trials without a billing provider */}
                {subscriptionStatus.billingProvider !== 'apple' && subscriptionStatus.trialEnd && (subscriptionStatus.isTrialing || subscriptionStatus.isTrialExpired) && (
                  <div>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '2px' }}>
                      {subscriptionStatus.isTrialExpired ? 'Trial Ended' : 'Trial Ends'}
                    </div>
                    <div style={{ fontSize: '14px', color: subscriptionStatus.isTrialExpired ? 'var(--text-secondary)' : 'var(--text-primary)' }}>
                      {formatDateTime(subscriptionStatus.trialEnd)}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Action buttons */}
          {isOwner && (
            <div
              style={{
                marginTop: '20px',
                paddingTop: '20px',
                borderTop: '1px solid var(--border-light)',
              }}
            >
              {/* Apple-managed subscription — read-only in web app */}
              {subscriptionStatus?.billingProvider === 'apple' && (subscriptionStatus?.isActive || subscriptionStatus?.isTrialing) && (
                <div
                  style={{
                    padding: '16px',
                    backgroundColor: 'var(--bg-secondary)',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px',
                  }}
                >
                  <span style={{ fontSize: '20px', flexShrink: 0 }}>&#63743;</span>
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '4px' }}>
                      Managed through the App Store
                    </div>
                    <div style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                      Your subscription is managed through Apple. To upgrade, downgrade, or cancel,
                      open <strong>Settings &gt; Apple Account &gt; Subscriptions</strong> on your iPhone.
                    </div>
                  </div>
                </div>
              )}

              {/* Stripe-managed or no subscription — show existing Stripe UI */}
              {subscriptionStatus?.billingProvider !== 'apple' && (
                <>
                  {/* Trial eligible - show start trial button */}
                  {subscriptionStatus?.isEligibleForTrial && !subscriptionStatus?.status && (
                    <div style={{ marginBottom: '16px' }}>
                      <button
                        onClick={() => setShowTrialModal(true)}
                        disabled={actionLoading}
                        className="btn btn-primary"
                        style={{ fontSize: '14px', padding: '12px 24px' }}
                      >
                        {actionLoading ? 'Processing...' : `Start ${pricing?.trialDurationDays || 30}-Day Free Trial`}
                      </button>
                      <p style={{ margin: '8px 0 0', fontSize: '13px', color: 'var(--text-secondary)' }}>
                        No credit card required. Full access to all features.
                      </p>
                    </div>
                  )}

                  {/* No Stripe subscription - show subscription options */}
                  {!subscriptionStatus?.stripeSubscriptionId && pricing?.tiers && pricing.tiers.length > 0 && (
                    <div>
                      <button
                        onClick={() => setShowTierComparison(true)}
                        disabled={actionLoading}
                        className="btn btn-secondary"
                        style={{
                          fontSize: '14px',
                          padding: '12px 24px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                        }}
                      >
                        <span style={{ fontSize: '18px' }}>&#128202;</span>
                        <span style={{ fontWeight: 600 }}>View Subscription Plans</span>
                      </button>
                    </div>
                  )}

                  {/* Has Stripe subscription - show manage button */}
                  {subscriptionStatus?.stripeSubscriptionId && (
                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                      <button
                        onClick={handleManageSubscription}
                        disabled={actionLoading}
                        className="btn btn-primary"
                        style={{ fontSize: '14px', padding: '10px 20px' }}
                      >
                        Manage Subscription
                      </button>

                      {/* Upgrade button - show when subscription is active (not canceling) and pricing tiers are available */}
                      {!subscriptionStatus.cancelAtPeriodEnd && pricing?.tiers && pricing.tiers.length > 0 && (
                        <button
                          onClick={() => setShowTierComparison(true)}
                          disabled={actionLoading}
                          className="btn btn-secondary"
                          style={{ fontSize: '14px', padding: '10px 20px' }}
                        >
                          Upgrade Plan
                        </button>
                      )}

                      {subscriptionStatus.cancelAtPeriodEnd ? (
                        <button
                          onClick={handleReactivate}
                          disabled={actionLoading}
                          className="btn btn-secondary"
                          style={{ fontSize: '14px', padding: '10px 20px' }}
                        >
                          Reactivate Subscription
                        </button>
                      ) : (
                        <button
                          onClick={handleCancelSubscription}
                          disabled={actionLoading}
                          className="btn btn-ghost"
                          style={{ fontSize: '14px', padding: '10px 20px', color: 'var(--error-text)' }}
                        >
                          {actionLoading ? 'Processing...' : 'Cancel Subscription'}
                        </button>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {!isOwner && (
            <div
              style={{
                marginTop: '20px',
                padding: '12px 16px',
                backgroundColor: 'var(--bg-secondary)',
                borderRadius: '8px',
                fontSize: '14px',
                color: 'var(--text-secondary)',
              }}
            >
              Only shop owners can manage subscriptions.
            </div>
          )}
        </div>
      </div>

      {/* Resource Limits Card */}
      <div className="card">
        <div style={{ padding: '20px' }}>
          <h4 className="heading-secondary" style={{ fontSize: '16px', marginBottom: '20px' }}>
            Resource Limits
          </h4>

          {Object.keys(rateLimits).length === 0 ? (
            <p className="text-muted" style={{ fontSize: '14px' }}>
              No usage data available.
            </p>
          ) : (
            <div>
              {resourceTypesIn('standing').map(
                (resourceType) =>
                  rateLimits[resourceType] && (
                    <div key={resourceType}>
                      {renderUsageMeter(rateLimits[resourceType]!)}
                    </div>
                  )
              )}
            </div>
          )}

          <div
            style={{
              marginTop: '16px',
              padding: '12px 16px',
              backgroundColor: 'var(--bg-secondary)',
              borderRadius: '8px',
              fontSize: '13px',
              color: 'var(--text-primary)',
            }}
          >
            Current resource usage against your plan limits.
          </div>
        </div>
      </div>

      {/* Monthly Usage Card */}
      <div className="card" style={{ marginTop: '24px' }}>
        <div style={{ padding: '20px' }}>
          <h4 className="heading-secondary" style={{ fontSize: '16px', marginBottom: '20px' }}>
            Monthly Usage
          </h4>

          {Object.keys(rateLimits).length === 0 ? (
            <p className="text-muted" style={{ fontSize: '14px' }}>
              No usage data available.
            </p>
          ) : (
            <div>
              {resourceTypesIn('monthly').map(
                (resourceType) =>
                  rateLimits[resourceType] && (
                    <div key={resourceType}>
                      {renderUsageMeter(rateLimits[resourceType]!)}
                    </div>
                  )
              )}
            </div>
          )}

          <div
            style={{
              marginTop: '16px',
              padding: '12px 16px',
              backgroundColor: 'var(--bg-secondary)',
              borderRadius: '8px',
              fontSize: '13px',
              color: 'var(--text-primary)',
            }}
          >
            Usage resets at the beginning of each billing period. Upgrade your plan for higher limits.
          </div>
        </div>
      </div>

      {/* Tier Information */}
      {subscriptionStatus && (
        <div className="card" style={{ marginTop: '24px' }}>
          <div style={{ padding: '20px' }}>
            <h4 className="heading-secondary" style={{ fontSize: '16px', marginBottom: '16px' }}>
              Subscription Details
            </h4>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '16px',
              }}
            >
              <div>
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                  Tier Type
                </div>
                <div style={{ fontSize: '14px', color: 'var(--text-primary)' }}>
                  {currentShop?.type || 'Unknown'}
                </div>
              </div>
              {subscriptionStatus.stripeCustomerId && (
                <div>
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                    Stripe Customer
                  </div>
                  <div style={{ fontSize: '14px', color: 'var(--text-primary)', fontFamily: 'monospace' }}>
                    {subscriptionStatus.stripeCustomerId}
                  </div>
                </div>
              )}
              {subscriptionStatus.stripeSubscriptionId && (
                <div>
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                    Subscription ID
                  </div>
                  <div style={{ fontSize: '14px', color: 'var(--text-primary)', fontFamily: 'monospace' }}>
                    {subscriptionStatus.stripeSubscriptionId}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Tier Comparison Modal */}
      {showTierComparison && pricing?.tiers && (
        <TierComparisonModal
          tiers={pricing.tiers}
          currentTierType={subscriptionStatus?.stripeSubscriptionId ? currentShop?.type : undefined}
          onClose={() => setShowTierComparison(false)}
          onSubscribe={(priceId) => {
            // For existing subscribers, show confirmation before proceeding
            if (subscriptionStatus?.stripeSubscriptionId && pricing?.tiers) {
              const selectedTier = pricing.tiers.find(t => t.stripePriceId === priceId);
              if (selectedTier) {
                setShowTierComparison(false);
                setPendingPlanChange(selectedTier);
                return;
              }
            }
            setShowTierComparison(false);
            handleUpgrade(priceId);
          }}
          isLoading={actionLoading}
        />
      )}

      {/* Trial Modal - uses PERSONAL_TRIAL tier limits from pricing response */}
      {showTrialModal && pricing?.trialLimits && (
        <TrialModal
          trialDurationDays={pricing.trialDurationDays || 30}
          trialLimits={pricing.trialLimits}
          onClose={() => setShowTrialModal(false)}
          onStartTrial={() => {
            setShowTrialModal(false);
            handleActivateTrial();
          }}
          isLoading={actionLoading}
        />
      )}

      {/* Cancel Subscription Confirmation Modal */}
      {showCancelConfirm && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1100,
            padding: '20px',
          }}
          onClick={() => setShowCancelConfirm(false)}
        >
          <div
            style={{
              backgroundColor: 'var(--bg-primary)',
              borderRadius: '12px',
              padding: '24px',
              maxWidth: '440px',
              width: '100%',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ margin: '0 0 16px 0', fontSize: '20px', fontWeight: 600, color: 'var(--text-primary)' }}>
              Cancel Subscription
            </h2>

            <p style={{ fontSize: '14px', color: 'var(--text-primary)', lineHeight: 1.6, margin: '0 0 16px 0' }}>
              Are you sure you want to cancel your <strong>{currentShop?.type}</strong> subscription?
            </p>

            <p style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.5, margin: '0 0 24px 0' }}>
              You will retain access to all features until the end of your current billing period
              {subscriptionStatus?.currentPeriodEnd ? <> on <strong>{formatDateTime(subscriptionStatus.currentPeriodEnd)}</strong></> : ''}.
            </p>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => setShowCancelConfirm(false)}
                style={{
                  flex: 1,
                  padding: '12px 20px',
                  backgroundColor: 'var(--bg-secondary)',
                  color: 'var(--text-primary)',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                Keep Subscription
              </button>
              <button
                onClick={confirmCancelSubscription}
                style={{
                  flex: 1,
                  padding: '12px 20px',
                  backgroundColor: 'var(--error)',
                  color: 'var(--text-inverse)',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Cancel Subscription
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Plan Change Confirmation Modal */}
      {pendingPlanChange && (() => {
        const currentTierPrice = pricing?.tiers.find(t => t.tierType === currentShop?.type)?.monthlyPriceUSD ?? 0;
        const isUpgrade = pendingPlanChange.monthlyPriceUSD > currentTierPrice;
        const changeLabel = isUpgrade ? 'Upgrade' : 'Downgrade';

        return (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1100,
              padding: '20px',
            }}
            onClick={() => setPendingPlanChange(null)}
          >
            <div
              style={{
                backgroundColor: 'var(--bg-primary)',
                borderRadius: '12px',
                padding: '24px',
                maxWidth: '440px',
                width: '100%',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h2 style={{ margin: '0 0 16px 0', fontSize: '20px', fontWeight: 600, color: 'var(--text-primary)' }}>
                Confirm {changeLabel}
              </h2>

              <p style={{ fontSize: '14px', color: 'var(--text-primary)', lineHeight: 1.6, margin: '0 0 16px 0' }}>
                {isUpgrade
                  ? <>You are upgrading from <strong>{currentShop?.type}</strong> to <strong>{pendingPlanChange.displayName}</strong> at <strong>${pendingPlanChange.monthlyPriceUSD}/mo</strong>.</>
                  : <>You are downgrading from <strong>{currentShop?.type}</strong> to <strong>{pendingPlanChange.displayName}</strong> at <strong>${pendingPlanChange.monthlyPriceUSD}/mo</strong>.</>
                }
              </p>

              <p style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.5, margin: '0 0 24px 0' }}>
                {isUpgrade
                  ? 'The price difference will be prorated for the remainder of your current billing period.'
                  : 'A credit will be applied to your account for the remainder of your current billing period.'}
              </p>

              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={() => setPendingPlanChange(null)}
                  disabled={actionLoading}
                  style={{
                    flex: 1,
                    padding: '12px 20px',
                    backgroundColor: 'var(--bg-secondary)',
                    color: 'var(--text-primary)',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '15px',
                    fontWeight: 500,
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    const priceId = pendingPlanChange.stripePriceId;
                    setPendingPlanChange(null);
                    handleUpgrade(priceId);
                  }}
                  disabled={actionLoading}
                  style={{
                    flex: 1,
                    padding: '12px 20px',
                    backgroundColor: 'var(--primary)',
                    color: 'var(--text-inverse)',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '15px',
                    fontWeight: 600,
                    cursor: actionLoading ? 'not-allowed' : 'pointer',
                    opacity: actionLoading ? 0.7 : 1,
                  }}
                >
                  {actionLoading ? 'Processing...' : `Confirm ${changeLabel}`}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default SubscriptionManager;
